import { Button, Text, TextChip, Tooltip } from '@shared/components';
import { AssigneeOptionVariant } from '@shared/components/dropdown-chip/DropdownChip';
import { Option, Select, TextField } from '@shared/components/react-hook-form';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useNegotiationStrategies } from '@shared/graphql/hooks/negotiationStrategy/useNegotiationStrategies';
import { NegotiationStrategyOverrideData } from '@shared/types/movement';
import { CarrierNoBudgeBehavior } from 'clerk_common/types/negotiationStrategy';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  DEFAULT_OPTION_VALUE,
  getMovementOverrideDropdownStrategies,
  getSelectableMovementOverrideStrategies,
  NEVER_BUDGE_INLINE_ID,
} from './negotiationStrategyOverrideOptions';
import { STRATEGY_TYPE_LABELS } from './strategyLabels';

const CARRIER_NO_BUDGE_BEHAVIOR_DEFAULT = 'DEFAULT';
type CarrierNoBudgeBehaviorFormValue =
  | CarrierNoBudgeBehavior
  | typeof CARRIER_NO_BUDGE_BEHAVIOR_DEFAULT;

type StrategyFormValues = {
  selectedStrategyId: string;
  autoRejectThreshold: string;
  incrementAmount: string;
  incrementPercentage: string;
  initialIncrementAmount: string;
  firstOfferBelowTargetCounterPercent: string;
  firstOfferBelowTargetCounterDollars: string;
  carrierNoBudgeBehavior: CarrierNoBudgeBehaviorFormValue;
};

const CARRIER_NO_BUDGE_BEHAVIOR_OPTIONS: {
  value: CarrierNoBudgeBehaviorFormValue;
  label: string;
}[] = [
  {
    value: CARRIER_NO_BUDGE_BEHAVIOR_DEFAULT,
    label: 'Default (from strategy)',
  },
  { value: CarrierNoBudgeBehavior.DONT_BUDGE, label: "Don't budge" },
  { value: CarrierNoBudgeBehavior.NEGOTIATE, label: 'Negotiate' },
];

const TYPES_WITH_NO_BUDGE_BEHAVIOR = [
  'FIXED_AMOUNT',
  'PERCENTAGE_BASED',
  'ESCALATION',
  'SMART_PRICING',
];

const STRATEGY_CONFIG_FIELDS: Record<
  string,
  { field: keyof StrategyFormValues; label: string; tooltip: string }[]
> = {
  FIXED_AMOUNT: [
    {
      field: 'incrementAmount',
      label: 'Increment Amount ($)',
      tooltip: 'Amount to increase each counter-offer by',
    },
  ],
  PERCENTAGE_BASED: [
    {
      field: 'incrementPercentage',
      label: 'Increment Percentage (%)',
      tooltip: 'Percentage to increase each counter-offer by',
    },
  ],
  ESCALATION: [
    {
      field: 'initialIncrementAmount',
      label: 'Initial Increment ($)',
      tooltip: 'Starting counter-offer increment before escalation',
    },
  ],
};

const AUTO_REJECT_THRESHOLD_TOOLTIP =
  'Maximum amount above max price before the agent automatically rejects the carrier offer';

const TYPES_WITHOUT_AUTO_REJECT_THRESHOLD = ['ALWAYS_ACCEPT', 'ALWAYS_DECLINE'];

type NegotiationStrategyOverrideProps = {
  movementId: string;
  currentOverride?: NegotiationStrategyOverrideData;
  updateMovement: (input: {
    id: string;
    negotiationStrategyConfigOverride?: {
      strategyId?: string;
      strategyType?: string;
      config?: Record<string, unknown>;
    } | null;
  }) => void;
  isLoading: boolean;
};

export function NegotiationStrategyOverride({
  movementId,
  currentOverride,
  updateMovement,
  isLoading,
}: NegotiationStrategyOverrideProps) {
  const { defaultOrgId } = useMeContext();
  const { strategies, loading: strategiesLoading } = useNegotiationStrategies({
    organizationId: defaultOrgId,
  });

  const selectableStrategies = useMemo(
    () =>
      getSelectableMovementOverrideStrategies(
        strategies,
        currentOverride?.strategyId
      ),
    [strategies, currentOverride?.strategyId]
  );

  const hasOrgNeverBudge = useMemo(
    () => selectableStrategies.some((s) => s.type === 'NEVER_BUDGE'),
    [selectableStrategies]
  );

  const dropdownStrategies = useMemo(
    () => getMovementOverrideDropdownStrategies(selectableStrategies),
    [selectableStrategies]
  );

  const overrideActive = !!currentOverride;

  const initialStrategyId = useMemo(() => {
    if (!currentOverride) return DEFAULT_OPTION_VALUE;
    if (currentOverride.strategyId) return currentOverride.strategyId;
    if (currentOverride.strategyType === 'NEVER_BUDGE') {
      if (!hasOrgNeverBudge) return NEVER_BUDGE_INLINE_ID;
      const orgNeverBudge = selectableStrategies.find(
        (s) => s.type === 'NEVER_BUDGE'
      );
      if (orgNeverBudge) return orgNeverBudge.id;
    }
    return DEFAULT_OPTION_VALUE;
  }, [currentOverride, hasOrgNeverBudge, selectableStrategies]);

  const { control, watch, reset, getValues } = useForm<StrategyFormValues>({
    defaultValues: {
      selectedStrategyId: initialStrategyId,
      autoRejectThreshold: '',
      incrementAmount: '',
      incrementPercentage: '',
      initialIncrementAmount: '',
      firstOfferBelowTargetCounterPercent: '',
      firstOfferBelowTargetCounterDollars: '',
      carrierNoBudgeBehavior: CARRIER_NO_BUDGE_BEHAVIOR_DEFAULT,
    },
  });

  const selectedStrategyId = watch('selectedStrategyId');
  const counterPercentValue = watch('firstOfferBelowTargetCounterPercent');
  const counterDollarsValue = watch('firstOfferBelowTargetCounterDollars');

  const selectedStrategy = useMemo(
    () => dropdownStrategies.find((s) => s.id === selectedStrategyId),
    [dropdownStrategies, selectedStrategyId]
  );

  const selectedType = selectedStrategy?.type;

  useEffect(() => {
    reset({
      selectedStrategyId: initialStrategyId,
      autoRejectThreshold: '',
      incrementAmount: '',
      incrementPercentage: '',
      initialIncrementAmount: '',
      firstOfferBelowTargetCounterPercent: '',
      firstOfferBelowTargetCounterDollars: '',
      carrierNoBudgeBehavior: CARRIER_NO_BUDGE_BEHAVIOR_DEFAULT,
    });
  }, [movementId, initialStrategyId, reset]);

  useEffect(() => {
    if (!selectedStrategy) {
      reset({
        selectedStrategyId: getValues('selectedStrategyId'),
        autoRejectThreshold: '',
        incrementAmount: '',
        incrementPercentage: '',
        initialIncrementAmount: '',
        firstOfferBelowTargetCounterPercent: '',
        firstOfferBelowTargetCounterDollars: '',
        carrierNoBudgeBehavior: CARRIER_NO_BUDGE_BEHAVIOR_DEFAULT,
      });
      return;
    }

    const config = selectedStrategy.config as Record<string, unknown>;

    const isCurrentOverride =
      overrideActive &&
      (currentOverride?.strategyId === selectedStrategy.id ||
        (currentOverride?.strategyType === selectedStrategy.type &&
          !currentOverride?.strategyId));
    const source = isCurrentOverride
      ? (currentOverride?.config as Record<string, unknown>) ?? config
      : config;

    reset({
      selectedStrategyId: selectedStrategy.id,
      autoRejectThreshold:
        source?.autoRejectThreshold != null
          ? String(source.autoRejectThreshold)
          : '',
      incrementAmount:
        source?.incrementAmount != null ? String(source.incrementAmount) : '',
      incrementPercentage:
        source?.incrementPercentage != null
          ? String(source.incrementPercentage)
          : '',
      initialIncrementAmount:
        source?.initialIncrementAmount != null
          ? String(source.initialIncrementAmount)
          : '',
      firstOfferBelowTargetCounterPercent:
        source?.firstOfferBelowTargetCounterPercent != null
          ? String(source.firstOfferBelowTargetCounterPercent)
          : '',
      firstOfferBelowTargetCounterDollars:
        source?.firstOfferBelowTargetCounterDollars != null
          ? String(source.firstOfferBelowTargetCounterDollars)
          : '',
      carrierNoBudgeBehavior:
        source?.carrierNoBudgeBehavior === CarrierNoBudgeBehavior.NEGOTIATE ||
        source?.carrierNoBudgeBehavior === CarrierNoBudgeBehavior.DONT_BUDGE
          ? (source.carrierNoBudgeBehavior as CarrierNoBudgeBehavior)
          : CARRIER_NO_BUDGE_BEHAVIOR_DEFAULT,
    });
  }, [selectedStrategy?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApply = () => {
    const values = getValues();
    const strategy = dropdownStrategies.find(
      (s) => s.id === values.selectedStrategyId
    );
    if (!strategy) return;

    const config = buildConfigPayload(strategy.type, values);

    if (strategy.id === NEVER_BUDGE_INLINE_ID) {
      updateMovement({
        id: movementId,
        negotiationStrategyConfigOverride: {
          strategyType: 'NEVER_BUDGE',
          config: { type: 'NEVER_BUDGE', ...config },
        },
      });
    } else {
      updateMovement({
        id: movementId,
        negotiationStrategyConfigOverride: {
          strategyId: strategy.id,
          ...(Object.keys(config).length > 0 ? { config } : {}),
        },
      });
    }
  };

  const handleClear = () => {
    updateMovement({
      id: movementId,
      negotiationStrategyConfigOverride: null,
    });
    reset({
      selectedStrategyId: DEFAULT_OPTION_VALUE,
      autoRejectThreshold: '',
      incrementAmount: '',
      incrementPercentage: '',
      initialIncrementAmount: '',
      firstOfferBelowTargetCounterPercent: '',
      firstOfferBelowTargetCounterDollars: '',
      carrierNoBudgeBehavior: CARRIER_NO_BUDGE_BEHAVIOR_DEFAULT,
    });
  };

  const isDefault = selectedStrategyId === DEFAULT_OPTION_VALUE;
  const showAutoRejectThreshold =
    selectedType && !TYPES_WITHOUT_AUTO_REJECT_THRESHOLD.includes(selectedType);
  const typeSpecificFields = selectedType
    ? STRATEGY_CONFIG_FIELDS[selectedType] ?? []
    : [];

  const renderStrategyValue = (value: string) => {
    if (value === DEFAULT_OPTION_VALUE) return 'Default (from SOP)';
    const strategy = dropdownStrategies.find((s) => s.id === value);
    if (!strategy) return 'Default (from SOP)';
    return strategy.active ? strategy.name : `${strategy.name} (Inactive)`;
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-2">
      <Select
        name="selectedStrategyId"
        control={control}
        label={
          <div className="flex items-center gap-1.5">
            <Text type="body-sm">Negotiation Strategy</Text>
            {overrideActive && (
              <TextChip
                text="Override Active"
                variant={AssigneeOptionVariant.WARNING}
                shouldCapitalize={false}
              />
            )}
          </div>
        }
        aria-label="Negotiation Strategy"
        size="small"
        isDisabled={isLoading || strategiesLoading}
        renderValue={renderStrategyValue}
      >
        {[
          <Option key="__default__" value={DEFAULT_OPTION_VALUE}>
            Default (from SOP)
          </Option>,
          ...dropdownStrategies.map((s) => (
            <Option key={s.id} value={s.id}>
              <span>
                {s.name}
                <span className="ml-1.5 text-gray-400">
                  {STRATEGY_TYPE_LABELS[s.type] ?? s.type}
                  {!s.active && ' - Inactive'}
                </span>
              </span>
            </Option>
          )),
        ]}
      </Select>

      {!isDefault && showAutoRejectThreshold && (
        <Tooltip content={AUTO_REJECT_THRESHOLD_TOOLTIP}>
          <div>
            <TextField
              label={<Text type="body-xs">Auto-Reject Threshold ($)</Text>}
              aria-label="Auto-Reject Threshold"
              name="autoRejectThreshold"
              control={control}
              type="number"
              size="small"
              inputWrapperClassName="border-r drop-shadow-none"
              fieldWrapperClassName="gap-0"
              prepend={
                <Text type="body-xs" className="pl-[8px] text-gray-500">
                  $
                </Text>
              }
            />
          </div>
        </Tooltip>
      )}

      {!isDefault && (
        <div className="flex gap-2">
          <Tooltip content="Counter below the carrier's first offer by this percentage when it's below target">
            <div className="flex-1">
              <TextField
                label={<Text type="body-xs">1st Offer Counter (%)</Text>}
                aria-label="First Offer Below Target Counter Percent"
                name="firstOfferBelowTargetCounterPercent"
                control={control}
                type="number"
                size="small"
                isDisabled={!!counterDollarsValue}
                inputWrapperClassName="border-r drop-shadow-none"
                fieldWrapperClassName="gap-0"
                prepend={
                  <Text type="body-xs" className="pl-[8px] text-gray-500">
                    %
                  </Text>
                }
              />
            </div>
          </Tooltip>
          <Tooltip content="Counter below the carrier's first offer by this dollar amount when it's below target">
            <div className="flex-1">
              <TextField
                label={<Text type="body-xs">1st Offer Counter ($)</Text>}
                aria-label="First Offer Below Target Counter Dollars"
                name="firstOfferBelowTargetCounterDollars"
                control={control}
                type="number"
                size="small"
                isDisabled={!!counterPercentValue}
                inputWrapperClassName="border-r drop-shadow-none"
                fieldWrapperClassName="gap-0"
                prepend={
                  <Text type="body-xs" className="pl-[8px] text-gray-500">
                    $
                  </Text>
                }
              />
            </div>
          </Tooltip>
        </div>
      )}

      {!isDefault &&
        typeSpecificFields.map(({ field, label, tooltip }) => (
          <Tooltip key={field} content={tooltip}>
            <div>
              <TextField
                label={<Text type="body-xs">{label}</Text>}
                aria-label={label}
                name={field}
                control={control}
                type="number"
                size="small"
                inputWrapperClassName="border-r drop-shadow-none"
                fieldWrapperClassName="gap-0"
                prepend={
                  <Text type="body-xs" className="pl-[8px] text-gray-500">
                    {field === 'incrementPercentage' ? '%' : '$'}
                  </Text>
                }
              />
            </div>
          </Tooltip>
        ))}

      {!isDefault &&
        selectedType &&
        TYPES_WITH_NO_BUDGE_BEHAVIOR.includes(selectedType) && (
          <Tooltip content="When the carrier stops moving on price, choose whether to reiterate (default) or keep negotiating with another counter offer.">
            <div>
              <Select
                name="carrierNoBudgeBehavior"
                control={control}
                label={<Text type="body-xs">When Carrier Stops Budging</Text>}
                aria-label="When Carrier Stops Budging"
                size="small"
              >
                {CARRIER_NO_BUDGE_BEHAVIOR_OPTIONS.map((o) => (
                  <Option key={o.value} value={o.value}>
                    {o.label}
                  </Option>
                ))}
              </Select>
            </div>
          </Tooltip>
        )}

      {!isDefault && (
        <div className="flex gap-2">
          <Button
            size="xs"
            variant="primary"
            onPress={handleApply}
            isDisabled={isLoading}
          >
            Apply
          </Button>
          {overrideActive && (
            <Button
              size="xs"
              variant="tertiary"
              onPress={handleClear}
              isDisabled={isLoading}
            >
              Clear
            </Button>
          )}
        </div>
      )}

      {isDefault && overrideActive && (
        <Button
          size="xs"
          variant="tertiary"
          onPress={handleClear}
          isDisabled={isLoading}
        >
          Clear Override
        </Button>
      )}
    </div>
  );
}

function buildConfigPayload(
  strategyType: string,
  values: StrategyFormValues
): Record<string, unknown> {
  const config: Record<string, unknown> = {};

  if (values.autoRejectThreshold) {
    config.autoRejectThreshold = parseFloat(values.autoRejectThreshold);
  }

  if (values.firstOfferBelowTargetCounterPercent) {
    config.firstOfferBelowTargetCounterPercent = parseFloat(
      values.firstOfferBelowTargetCounterPercent
    );
  } else if (values.firstOfferBelowTargetCounterDollars) {
    config.firstOfferBelowTargetCounterDollars = parseFloat(
      values.firstOfferBelowTargetCounterDollars
    );
  }

  if (
    TYPES_WITH_NO_BUDGE_BEHAVIOR.includes(strategyType) &&
    values.carrierNoBudgeBehavior &&
    values.carrierNoBudgeBehavior !== CARRIER_NO_BUDGE_BEHAVIOR_DEFAULT
  ) {
    config.carrierNoBudgeBehavior = values.carrierNoBudgeBehavior;
  }

  switch (strategyType) {
    case 'FIXED_AMOUNT':
      if (values.incrementAmount) {
        config.incrementAmount = parseFloat(values.incrementAmount);
      }
      break;
    case 'PERCENTAGE_BASED':
      if (values.incrementPercentage) {
        config.incrementPercentage = parseFloat(values.incrementPercentage);
      }
      break;
    case 'ESCALATION':
      if (values.initialIncrementAmount) {
        config.initialIncrementAmount = parseFloat(
          values.initialIncrementAmount
        );
      }
      break;
  }

  return config;
}
